import { z } from 'zod';

import type { ProcessCategory } from './process-category';

export const RFQ_SUGGESTION_CATEGORIES = ['recommended', 'suggested', 'admin_picked'] as const;
export type RfqSuggestionCategory = (typeof RFQ_SUGGESTION_CATEGORIES)[number];

export const RFQ_SUGGESTION_LISTS = [...RFQ_SUGGESTION_CATEGORIES, 'others', 'removed'] as const;
export type RfqSuggestionList = (typeof RFQ_SUGGESTION_LISTS)[number];
export const rfqSuggestionListSchema = z.enum(RFQ_SUGGESTION_LISTS);

export const RFQ_MATCH_FLAG_CODES = [
  'PART_NO_PROCESS',
  'PART_PROCESS_UNMAPPED',
  'PART_MATERIAL_UNKNOWN',
  'SUPPLIER_MATERIALS_UNDECLARED',
  'PROFILE_ONLY',
  'PARTIAL_COVERAGE',
] as const;
export type RfqMatchFlagCode = (typeof RFQ_MATCH_FLAG_CODES)[number];

export interface RfqMatchFlag {
  code: RfqMatchFlagCode;
  partId?: string;
  text: string;
}

export interface RfqMatchReason {
  partId: string | null;
  text: string;
}

export const DESIGN_CAD_ROOT_NODE = 'eng_design_cad';

export type RfqProcessRule =
  | { kind: 'additive'; category: ProcessCategory }
  | { kind: 'nodes'; nodes: readonly string[] };

export const RFQ_PROCESS_RULES: Record<string, RfqProcessRule> = {
  'CNC Machining': {
    kind: 'nodes',
    nodes: ['sub_cnc_milling', 'sub_cnc_turning', 'sub_5_axis_machining', 'sub_swiss_machining'],
  },
  'Sheet Metal': { kind: 'nodes', nodes: ['form_sheet_metal', 'form_press_brake'] },
  'Injection Molding': { kind: 'nodes', nodes: ['cast_injection_molding'] },
  'Die Casting': { kind: 'nodes', nodes: ['cast_die_casting'] },
  'Urethane Casting': { kind: 'nodes', nodes: ['cast_urethane_casting'] },
  'FDM — 3D Printing': { kind: 'additive', category: 'material_extrusion' },
  'SLS/MJF — 3D Printing': { kind: 'additive', category: 'powder_bed_fusion_polymer' },
  'SLA — 3D Printing': { kind: 'additive', category: 'vat_photopolymerization' },
  'Metal PBF — 3D Printing': { kind: 'additive', category: 'powder_bed_fusion_metal' },
};

/**
 * Rule for one stored process label.
 * @param processType The requirement's `processType`, or null when unset.
 * @returns The rule, or undefined when the label is empty or unknown.
 */
export function ruleForProcess(processType: string | null): RfqProcessRule | undefined {
  return processType ? RFQ_PROCESS_RULES[processType] : undefined;
}
